import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, ArticleDTO } from "../api";
import Builder from "../builder/Builder";
import { Doc, Head } from "../builder/types";
import { useAutosave, SaveStatus } from "../hooks";

const EMPTY_HEAD = {} as Head;

const statusLabel: Record<SaveStatus, string> = {
  idle: "",
  saving: "Saving…",
  saved: "Saved",
  error: "Save failed",
};

export default function ArticleEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState<ArticleDTO | null>(null);
  const [title, setTitle] = useState("");
  const [doc, setDoc] = useState<Doc | null>(null);
  const [mode, setMode] = useState<"edit" | "read">("edit");
  const [theme, setTheme] = useState<"dark" | "light">(() => (localStorage.getItem("vox-theme") as any) || "dark");
  const [err, setErr] = useState<string | null>(null);

  const { status, schedule } = useAutosave<{ title: string; blocks: any[] }>((p) => api.saveArticle(id!, p));

  useEffect(() => {
    if (!id) return;
    api
      .getArticle(id)
      .then((a) => {
        setArticle(a);
        setTitle(a.title);
        setDoc({ head: EMPTY_HEAD, blocks: a.blocks });
      })
      .catch((e) => setErr(e.message));
  }, [id]);

  const onDocChange = (next: Doc) => {
    setDoc(next);
    schedule({ title, blocks: next.blocks });
  };
  const onTitleChange = (t: string) => {
    setTitle(t);
    if (doc) schedule({ title: t, blocks: doc.blocks });
  };

  const submit = async () => {
    if (!article) return;
    const withdraw = article.status === "submitted";
    const updated = await api.submitArticle(article.id, withdraw);
    setArticle(updated);
  };

  if (err)
    return (
      <div className="page-wrap" style={{ textAlign: "center" }}>
        <p style={{ color: "var(--accent)" }}>{err}</p>
        <Link to="/articles" className="btn">← Back</Link>
      </div>
    );
  if (!doc || !article) return <div className="mono" style={{ padding: 60, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>Loading…</div>;

  return (
    <div>
      <div className="no-print" style={{ background: "var(--paper)", borderBottom: "1px solid var(--rule2)" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", flexWrap: "wrap" }}>
          <button className="btn" onClick={() => navigate("/articles")}>← Articles</button>
          <input
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Article title"
            style={{
              flex: 1,
              minWidth: 180,
              background: "transparent",
              border: "1px solid var(--rule2)",
              color: "var(--ink)",
              font: "600 14px 'Zilla Slab',sans-serif",
              padding: "7px 10px",
              borderRadius: 2,
            }}
          />
          <span className="mono" style={{ fontSize: 10, color: status === "error" ? "var(--accent)" : "var(--muted)", minWidth: 60 }}>
            {statusLabel[status]}
          </span>
          <button className="btn" onClick={() => setMode(mode === "edit" ? "read" : "edit")} style={mode === "edit" ? { borderColor: "var(--accent)", color: "var(--accent)" } : undefined}>
            {mode === "edit" ? "✓ Editing" : "✎ Edit"}
          </button>
          <button className="btn" onClick={() => { const t = theme === "dark" ? "light" : "dark"; setTheme(t); localStorage.setItem("vox-theme", t); }}>
            {theme === "dark" ? "Light" : "Dark"}
          </button>
          <button className="btn btn-accent" onClick={submit}>
            {article.status === "submitted" ? "Withdraw" : "Submit ▸"}
          </button>
        </div>
      </div>

      <div style={{ padding: "20px 0 60px" }}>
        <Builder doc={doc} variant="article" mode={mode} theme={theme} onChange={onDocChange} onUploadImage={api.uploadImage} />
      </div>
    </div>
  );
}
